<script setup lang="ts">
import { ref, onMounted, reactive } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  AtomInput,
  AtomInputMoney,
  SatSelect,
  SatButton,
  SatFormControl,
} from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useGiftCardStore } from "~/stores/giftCard";
import type { IDomainMoney } from "~/domain/money";
import type { IDomainGiftCardProvider } from "~/domain/gift-card-provider";

const emits = defineEmits<{ close: [MouseEvent] }>();

const css = bemBuilder("apply-gift-card");

const { t } = useLocale();

const basket = useBasketStore();
const giftCardStore = useGiftCardStore();

const isApplyingGiftCard = ref(false);
const errorMessage = ref("");

type IGiftCard = {
  provider: IDomainGiftCardProvider;
  code: string;
  pin?: string;
  amount?: IDomainMoney;
};

const giftCard: IGiftCard = reactive({
  provider: {
    id: "",
    name: "",
  },
  code: "",
  pin: undefined,
  amount: undefined,
});

const onSelectProvider = (provider: IDomainGiftCardProvider) => {
  giftCard.provider = provider;
};

onMounted(() => {
  if (giftCardStore.providers.length === 1) {
    giftCard.provider = giftCardStore.providers[0];
  }
});

const apply = async (event: MouseEvent) => {
  errorMessage.value = "";
  isApplyingGiftCard.value = true;

  if (!giftCard.amount || giftCard.amount.value <= 0) {
    errorMessage.value = t("components.apply_gift_card.error_amount_message");
    isApplyingGiftCard.value = false;
    return;
  }

  try {
    await basket.applyGiftCard({
      cardNumber: giftCard.code,
      amount: giftCard.amount.value,
      pin: giftCard.pin || undefined,
      providerId: giftCard.provider.id,
    });

    emits("close", event);
  } catch {
    errorMessage.value = t("components.apply_gift_card.error_message");
  } finally {
    isApplyingGiftCard.value = false;
  }
};
</script>

<template>
  <div :class="css()">
    <AtomText type="h3" color="color-text-headline">
      {{ t("components.apply_gift_card.title") }}
    </AtomText>
    <div :class="css('form')">
      <SatSelect
        v-if="giftCardStore.providers.length > 1"
        :options="giftCardStore.providers"
        size="large"
        data-test="provider-selector"
        :placeholder="
          t('components.apply_gift_card.select_provider_placeholder')
        "
        :has-errors="false"
        :disabled="false"
        :append-to-body="true"
        :value="giftCard.provider"
        @select="onSelectProvider"
      />
      <SatFormControl>
        <AtomInput
          :value="giftCard.code"
          data-test="code"
          :placeholder="t('components.apply_gift_card.code_placeholder')"
          @keyup="(value) => (giftCard.code = value)"
        />
      </SatFormControl>
      <div :class="css('amount-block')">
        <SatFormControl :class="css('form-control')">
          <AtomInputMoney
            :has-error="false"
            :disabled="false"
            :value="{
              value: giftCard.amount?.value || 0,
              currency: basket.currency,
            }"
            @input="(value) => (giftCard.amount = value)"
          />
        </SatFormControl>
        <SatFormControl :class="css('form-control')">
          <AtomInput
            data-test="pin"
            :value="giftCard.pin"
            :placeholder="t('components.apply_gift_card.pin_placeholder')"
            @keyup="(value) => (giftCard.pin = value)"
          />
        </SatFormControl>
      </div>
      <AtomText
        v-if="errorMessage"
        color="color-error"
        data-test="error-message"
      >
        {{ errorMessage }}
      </AtomText>
    </div>
    <SatButton
      size="large"
      theme="filled"
      variant="primary"
      data-test="apply-cta"
      :is-loading="isApplyingGiftCard"
      :is-disabled="!Boolean(giftCard.code)"
      @click.prevent="apply($event)"
    >
      {{ t("components.apply_gift_card.apply_cta") }}
    </SatButton>
  </div>
</template>

<style lang="scss" scoped>
.apply-gift-card {
  display: flex;
  gap: var(--spacing-lg);
  flex-direction: column;

  &__form {
    display: flex;
    gap: var(--spacing-md);
    flex-direction: column;
  }

  &__amount-block {
    display: flex;
    gap: var(--spacing-md);
  }

  &__form-control {
    width: 100%;
  }
}
</style>
